<div class="p-6 flex-table-page" data-testid="online-store-page">
  <!-- Header -->
  <h2 class="text-2xl font-semibold text-gray-800 mb-3 flex-table-header">
    {{ "onlineStore.management.title" | translate }}
  </h2>

  <app-search-form
    data-testid="online-store-search-form"
    [searchFormat]="searchConfig"
    (formSearch)="onSearch($event)"
    (formReset)="onReset()"
  >
    <div slot="actions" class="flex gap-2">
      <app-button
        [testId]="'online-store-column-config-btn'"
        type="success"
        [outlined]="true"
        icon="view_column"
        (emitOnClick)="onColumnConfigClick($event)"
      ></app-button>
      <app-status-filter
        [primaryButton]="primaryButton()"
        [secondaryButton]="secondaryButton()"
        (primaryButtonClick)="onAdd()"
        (secondaryButtonClick)="onBatchDelete()"
      ></app-status-filter>
    </div>
  </app-search-form>

  <!-- Data Table -->
  <div class="flex-table-section">
    <advanced-data-table
      data-testid="online-store-table"
      #advancedTable
      [dataSource]="onlineStores()"
      [defaultColumns]="defaultColumns()"
      [flexibleColumns]="flexibleColumns()"
      [loading]="loading()"
      [totalRecords]="totalRecords()"
      [rows]="searchParams().size"
      [first]="(searchParams().page - 1) * searchParams().size"
      [hasCheckColumn]="canDelete()"
      [selection]="selectedOnlineStores()"
      scrollHeight="flex"
      (selectionChange)="onSelectionChange($event)"
      (pageChange)="onPageChange($event)"
      [showInternalColumnConfigButton]="false"
    >
    <!-- Status: "Not connected" only for Lazada; other channels show active/inactive only -->
    <ng-template #statusTemplate let-status let-rowData="rowData">
      <div class="flex items-center justify-center gap-1">
        <p-tag
          data-testid="online-store-status-tag"
          [value]="
            getStatusDisplayText(status, rowData?.connectionStatus, rowData)
          "
          [severity]="
            getStatusSeverity(status, rowData?.connectionStatus, rowData)
          "
          styleClass="status-tag"
          [ngStyle]="getStatusStyle(status, rowData?.connectionStatus, rowData)"
          [pTooltip]="
            getStatusDisplayText(status, rowData?.connectionStatus, rowData)
          "
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </ng-template>

    <!-- Custom Connection Status Template -->
    <ng-template #connectionStatusTemplate let-connectionStatus>
      <div class="flex items-center justify-center gap-1">
        <p-tag
          data-testid="online-store-connection-status-tag"
          [value]="getConnectionStatusDisplayText(connectionStatus)"
          [severity]="getConnectionStatusSeverity(connectionStatus)"
          styleClass="status-tag"
          [ngStyle]="getConnectionStatusStyle(connectionStatus)"
          [pTooltip]="getConnectionStatusDisplayText(connectionStatus)"
          tooltipPosition="top"
          tooltipStyleClass="custom-tooltip"
          [showDelay]="300"
          [hideDelay]="0"
        />
      </div>
    </ng-template>

    <!-- Custom Template for different column types -->
    <ng-template
      #customTemplate
      let-value
      let-column="column"
      let-rowData="rowData"
    >
      @if (column.template === "dateTemplate") {
        <span class="text-xs text-gray-600">
          {{ formatDateTime(value) }}
        </span>
      } @else if (column.template === "channelTypeTemplate") {
        <span class="text-xs text-gray-600">
          {{ getChannelTypeLabel(value) }}
        </span>
      } @else if (column.template === "storeTypeTemplate") {
        <span class="text-xs text-gray-600">
          {{ getOnlineStoreTypeLabel(value) }}
        </span>
      } @else if (column?.template === "platformTemplate") {
        <span class="text-xs text-gray-600">
          {{ getPlatformLabel(rowData?.channelPlatform || value) }}
        </span>
      } @else if (column.template === "urlWebsiteTemplate") {
        <a
          [href]="value"
          target="_blank"
          rel="noopener noreferrer"
          class="text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer block truncate max-w-full"
          [pTooltip]="value"
          tooltipPosition="top"
          [showDelay]="300"
          title="{{ value }}"
        >
          {{ value }}
        </a>
      } @else if (column.template === "connectionStatusTemplate") {
        <ng-container
          *ngTemplateOutlet="
            connectionStatusTemplate;
            context: { $implicit: value }
          "
        ></ng-container>
      }
    </ng-template>

    <!-- Actions: Edit, Activate/Deactivate, View, Delete for all; Connect button only for Lazada when not connected -->
    <ng-template #actionsTemplate let-rowData>
      <div class="flex gap-2 justify-center">
        @if (canUpdate()) {
          <app-button
            [testId]="'online-store-action-edit'"
            class="action-edit"
            [label]="'common.actions.edit' | translate"
            type="action"
            (emitOnClick)="onEdit(rowData)"
          ></app-button>
        }
        @if (canApprove()) {
          <app-button
            [testId]="'online-store-action-toggle-status'"
            [class]="rowData.status ? 'action-disable' : 'action-enable'"
            [label]="
              rowData.status
                ? ('common.table.actions.deactivate' | translate)
                : ('common.table.actions.activate' | translate)
            "
            type="action"
            (emitOnClick)="onStatusChange(rowData, !rowData.status)"
          ></app-button>
        }
        @if (
          canApprove() &&
          isConnectableChannel(rowData) &&
          rowData.status &&
          (rowData.connectionStatus === "NOT_CONNECTED" ||
            !rowData.connectionStatus)
        ) {
          <app-button
            [testId]="'online-store-action-connect'"
            class="action-enable"
            [label]="'onlineStore.actions.connect' | translate"
            type="action"
            (emitOnClick)="onConnect(rowData)"
          ></app-button>
        }
        @if (canView()) {
          <app-button
            [testId]="'online-store-action-view'"
            class="action-view"
            [label]="'common.actions.view' | translate"
            type="action"
            (emitOnClick)="onView(rowData)"
          ></app-button>
        }
        @if (canDelete() && !rowData.status) {
          <app-button
            [testId]="'online-store-action-delete'"
            class="action-delete"
            [label]="'common.actions.delete' | translate"
            type="action"
            (emitOnClick)="onDelete(rowData)"
          ></app-button>
        }
      </div>
    </ng-template>
    </advanced-data-table>
  </div>

  <!-- Toast Messages -->
  <p-toast data-testid="online-store-toast"></p-toast>

  <!-- Custom Confirmation Dialog -->
  <app-confirmation-dialog
    data-testid="online-store-confirmation-dialog"
    [(visible)]="showConfirmationDialog"
    [title]="confirmationTitle()"
    [message]="confirmationMessage()"
    [buttons]="confirmationButtons()"
    (emitOnClick)="onConfirmationDialogAction($event)"
  ></app-confirmation-dialog>
</div>
